/**
 * @file src/components/organisms/YieldBearingAssetInvestmentActions/WithdrawAssetFormConfirmationReceipt.tsx
 * @description Defines the component for displaying the confirmation receipt for asset withdrawal.
 * This component shows details such as the withdrawn amount, remaining balance, and wallet addresses.
 * @module WithdrawAssetFormConfirmationReceipt
 */

import { WalletTokenListItem } from '@/components';
import CopyButton from '@/components/molecules/CopyButton/CopyButton';
import Table from '@/components/molecules/Table/Table';
import { TableRowProps } from '@/components/molecules/Table/interfaces/Table';
import { WalletTokenInterface } from '@/interfaces/WalletTokenInterface';
import {
  formatNumberToAmountInTokenCurrencyString,
  formatNumberToAmountInUsdString,
  formatStringToFirstAndLast4Characters,
} from '@/utils/formatUtils';
import { FC, useMemo } from 'react';
interface WithdrawAssetFormConfirmationReceiptProps {
  data: {
    externalWalletAddress: string;
    amount: number;
  };
  selectedAsset: WalletTokenInterface;
}

const WithdrawAssetFormConfirmationReceipt: FC<
  WithdrawAssetFormConfirmationReceiptProps
> = ({ data, selectedAsset }) => {
  const remainingBalance = useMemo(() => {
    const balance = selectedAsset.balance - data.amount;
    return balance < 0 ? 0 : balance;
  }, [data.amount, selectedAsset.balance]);

  const conversionRate = useMemo(() => {
    const balance = selectedAsset.balance || 0;
    const aggregatedBalanceInUsd = selectedAsset.priceInUsd
      ? selectedAsset.priceInUsd * balance
      : 0;
    return balance > 0 ? aggregatedBalanceInUsd / balance : 0;
  }, [selectedAsset.balance, selectedAsset.priceInUsd]);

  const remainingBalanceInUsd = useMemo(() => {
    return remainingBalance * conversionRate;
  }, [remainingBalance, conversionRate]);

  const rows: TableRowProps[] = [
    {
      cells: [
        { mainText: 'Asset' },
        {
          mainText: (
            <WalletTokenListItem
              token={selectedAsset}
              align="right"
              showWalletAddress={false}
              showBalance={false}
              showNetwork
              showNetworkPrefix
              showSubNetworkNextToMainNetwork
            />
          ),
          align: 'right',
        },
      ],
    },
    {
      cells: [
        { mainText: 'Amount' },
        {
          mainText: formatNumberToAmountInTokenCurrencyString(
            data.amount,
            selectedAsset.symbol || '',
          ),
          subText: formatNumberToAmountInUsdString(
            data.amount * conversionRate,
          ),
          align: 'right',
        },
      ],
    },
    {
      cells: [
        { mainText: 'Remaining Balance' },
        {
          mainText: formatNumberToAmountInTokenCurrencyString(
            remainingBalance,
            selectedAsset.symbol || '',
          ),
          subText: formatNumberToAmountInUsdString(remainingBalanceInUsd),
          align: 'right',
        },
      ],
    },
    {
      cells: [
        { mainText: 'Your Wallet' },
        {
          mainText: `Address ${formatStringToFirstAndLast4Characters(
            selectedAsset.walletAddress,
          )}`,
          subText: (
            <CopyButton
              value={selectedAsset.walletAddress}
              showLabel={false}
              displayValue={`Copy`}
              displayValueSize="sm"
              iconSize="sm"
            />
          ),
          align: 'right',
        },
      ],
    },
    {
      cells: [
        { mainText: 'Receiving Wallet' },
        {
          mainText: `Address ${formatStringToFirstAndLast4Characters(
            data.externalWalletAddress,
          )}`,
          subText: (
            <CopyButton
              value={data.externalWalletAddress}
              showLabel={false}
              displayValue={`Copy`}
              displayValueSize="sm"
              iconSize="sm"
            />
          ),
          align: 'right',
        },
      ],
    },
  ];

  return <Table columnWidths={[50, 50]} rows={rows} />;
};

export default WithdrawAssetFormConfirmationReceipt;
